import { useEffect, useRef, useState, useCallback } from "react";
import { Icon } from "../Common.jsx";
import { useApp } from "../../store.jsx";
import { useSession } from "../../lib/auth-client";
import { fmtRelative } from "../../lib/dates.js";

/**
 * Reusable support-chat panel.
 * Both the admin dashboard and the tenant chat bot mount this component;
 * the only difference is the API surface they hand in and the role we
 * stamp on outgoing optimistic messages.
 *
 * Props:
 *   ticket            : { id, ... }
 *   role              : 'admin' | 'user' — who we are
 *   api               : {
 *                          listMessages(ticketId): Promise<msg[]>,
 *                          postMessage(ticketId, body): Promise<msg>,
 *                       }
 *   locked            : boolean — disable the input (ticket closed)
 *   pollMs            : number — polling cadence (default 8000)
 *   onTicketUpdated() : optional callback after each successful send
 *   compact           : boolean — smaller chrome for the bot widget
 *   headerLabel       : override the header text
 */
export default function SupportChat({
  ticket,
  role,
  api,
  locked,
  pollMs = 8000,
  onTicketUpdated,
  compact = false,
  headerLabel,
}) {
  const { toast } = useApp();
  const { data: session } = useSession();
  const meId = session?.user?.id;
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listMessages(ticket.id);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [api, ticket.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (locked || !pollMs) return;
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [load, locked, pollMs]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    if (body.length > 4000) return toast("Message too long (max 4000)", "error");
    if (locked) return toast("Ticket is closed", "error");
    setSending(true);
    const optimistic = {
      id: `temp-${Date.now()}`,
      ticketId: ticket.id,
      senderId: meId,
      senderRole: role,
      body,
      createdAt: new Date().toISOString(),
      optimistic: true,
    };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    try {
      const saved = await api.postMessage(ticket.id, body);
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? saved : x)));
      onTicketUpdated?.();
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setDraft(body);
      if (e?.status === 429) {
        toast(e.message || "Slow down a moment", "warning");
      } else {
        toast(e.message || "Send failed", "error");
      }
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const inputPlaceholder = locked
    ? "Ticket closed"
    : role === "admin"
      ? "Reply to the customer…"
      : "Reply to support…";

  const height = compact ? 320 : 380;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height,
        border: "1px solid var(--border)",
        borderRadius: 12,
        overflow: "hidden",
        background: "var(--surface)",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--border)",
          fontSize: 12,
          color: "var(--text-muted)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span><Icon name="message-circle" size={12} /> {headerLabel || "Conversation"}</span>
        <span>{messages.length} messages</span>
      </div>
      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10, background: "var(--background, transparent)" }}
      >
        {loading ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center", padding: 24 }}>Loading…</div>
        ) : error ? (
          <div style={{ fontSize: 12, color: "var(--error)", textAlign: "center", padding: 24 }}>{error}</div>
        ) : messages.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center", padding: 24 }}>
            {role === "admin" ? "No messages yet. Send the first reply." : "Support will reply here. You can also send another message."}
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.senderRole === role;
            return (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  justifyContent: mine ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    maxWidth: "78%",
                    padding: "8px 12px",
                    borderRadius: 12,
                    background: mine ? "var(--primary, #7c3aed)" : "var(--surface-2, rgba(148,163,184,0.12))",
                    color: mine ? "#fff" : "var(--text)",
                    fontSize: 13,
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    opacity: m.optimistic ? 0.7 : 1,
                  }}
                >
                  {m.body}
                  <div
                    style={{
                      fontSize: 10,
                      marginTop: 4,
                      opacity: 0.75,
                      textAlign: "right",
                    }}
                  >
                    {m.senderRole === role ? "You" : m.senderRole === "admin" ? "Support" : m.senderRole}
                    {" · "}
                    {fmtRelative(m.createdAt)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {!locked && (
        <div
          style={{
            padding: 10,
            borderTop: "1px solid var(--border)",
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
          }}
        >
          <textarea
            className="input"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder={inputPlaceholder}
            disabled={sending}
            style={{ flex: 1, resize: "none", fontSize: 13 }}
          />
          <button
            className="btn btn-primary"
            onClick={send}
            disabled={sending || !draft.trim()}
          >
            <Icon name="send" size={14} /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
}
